import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Checkbox, Flex, Heading, Text, TextLink } from '@indeed/ifl-components';
import { MarkupComponents } from '@indeed/frontend-markup-react-components';
import { Information } from '@indeed/ifl-icons';
import GettextFactory from '../../../util/GettextFactory';

const SCROLL_BOTTOM_TOLERANCE_PX = 2;

const isScrolledToBottom = (element: HTMLElement): boolean =>
    element.scrollHeight - element.scrollTop - element.clientHeight <= SCROLL_BOTTOM_TOLERANCE_PX;

export interface ReviewDisclaimerProps {
    /** Heading shown at the top of the terms box (e.g. "Auto Apply (Beta) Terms"). */
    title: string;
    /**
     * Terms copy. Supports the `[label|privacyPolicyLink]` /
     * `[label|termsOfServiceLink]` markup tokens, which are rendered as links.
     */
    termsBody: string;
    /** Whether to render the consent checkbox below the terms. */
    showConsent: boolean;
    /** Label for the consent checkbox (a required asterisk is appended). */
    consentLabel: string;
    /** Current consent state (controlled by the parent). */
    checked: boolean;
    /** Called with the next consent value once the user has read the full terms. */
    onCheckedChange: (checked: boolean) => void;
}

/**
 * Reusable Auto Apply / Apply For Me terms disclaimer.
 *
 * Encapsulates the scrollable terms box, the "read to the bottom" gate, and the
 * optional consent checkbox so the same interaction can be reused with different
 * titles and terms copy (e.g. Apply For Me). The consent value is controlled by
 * the parent; the component only invokes `onCheckedChange` once the user has
 * scrolled through the full terms.
 */
export const ReviewDisclaimer: React.FC<ReviewDisclaimerProps> = ({
    title,
    termsBody,
    showConsent,
    consentLabel,
    checked,
    onCheckedChange,
}) => {
    const gt = GettextFactory();

    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [showScrollError, setShowScrollError] = useState(false);

    // Track ResizeObserver for cleanup.
    const resizeObserverRef = useRef<ResizeObserver | undefined>(undefined);
    const validationMessageRef = useRef<HTMLDivElement | null>(null);

    // Scroll the validation message into view whenever it is shown.
    useEffect(() => {
        if (showScrollError) {
            validationMessageRef.current?.scrollIntoView({ block: 'nearest' });
        }
    }, [showScrollError]);

    // Callback ref that sets up the observer when the element mounts. If the terms
    // fit without scrolling, the gate is satisfied immediately.
    const disclaimerBoxRef = useCallback((element: HTMLDivElement | null) => {
        resizeObserverRef.current?.disconnect();

        if (!element) {
            return;
        }

        // Latch the gate open (never reset to false) so a resize can't re-lock a user
        // who has already read to the bottom, which could otherwise trap them if the
        // box keeps resizing.
        const checkScrollability = () => {
            const needsScrolling = element.scrollHeight > element.clientHeight;
            if (!needsScrolling || isScrolledToBottom(element)) {
                setHasScrolledToBottom(true);
            }
        };

        checkScrollability();

        const resizeObserver = new ResizeObserver(checkScrollability);
        resizeObserver.observe(element);
        resizeObserverRef.current = resizeObserver;
    }, []);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const element = e.currentTarget;
        // Safari can report scrollTop beyond its nominal maximum during rubber-band
        // overscroll. A one-sided tolerance accepts that state and small rounding gaps.
        if (isScrolledToBottom(element) && !hasScrolledToBottom) {
            setHasScrolledToBottom(true);
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!hasScrolledToBottom && e.target.checked) {
            setShowScrollError(true);
            e.preventDefault();
            return;
        }
        setShowScrollError(false);
        onCheckedChange(e.target.checked);
    };

    return (
        <Box>
            <Box
                ref={disclaimerBoxRef}
                onScroll={handleScroll}
                // Keyboard-focusable so keyboard-only users can scroll the terms and
                // satisfy the scroll-to-bottom consent gate.
                tabIndex={0}
                sx={{
                    border: '1px solid',
                    borderColor: showScrollError ? 'border.status.critical' : 'border.weak',
                    borderRadius: 'lg',
                    padding: 4,
                    maxBlockSize: '300px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                }}
            >
                <Heading level={3} as="h2" sx={{ textAlign: 'center' }}>
                    {title}
                </Heading>
                <Text
                    level={2}
                    sx={{
                        whiteSpace: 'pre-line',
                    }}
                >
                    <MarkupComponents
                        components={{
                            privacyPolicyLink: (children) => (
                                <TextLink
                                    target="_blank"
                                    href="https://hrtechprivacy.com/brands/indeed#privacypolicy"
                                    inline
                                    rel="noopener noreferrer"
                                >
                                    {children}
                                </TextLink>
                            ),
                            termsOfServiceLink: (children) => (
                                <TextLink
                                    target="_blank"
                                    href="https://www.indeed.com/legal"
                                    inline
                                    rel="noopener noreferrer"
                                >
                                    {children}
                                </TextLink>
                            ),
                        }}
                    >
                        {termsBody}
                    </MarkupComponents>
                </Text>
            </Box>
            {showConsent && (
                <Box>
                    <Checkbox
                        checked={checked}
                        onChange={handleCheckboxChange}
                        sx={{
                            paddingBlockEnd: 0,
                            input: {
                                marginBlockStart: 1,
                                borderColor: showScrollError
                                    ? 'border.status.critical.strong'
                                    : undefined,
                                '&:hover': {
                                    borderColor: showScrollError
                                        ? 'border.status.critical.strong'
                                        : undefined,
                                },
                            },
                        }}
                    >
                        <Text level={2}>
                            {consentLabel}
                            <Text as="span" variant="alert" aria-hidden="true">
                                {' *'}
                            </Text>
                        </Text>
                    </Checkbox>
                    <Box role="alert">
                        {showScrollError && (
                            <Flex
                                ref={validationMessageRef}
                                sx={{
                                    alignItems: 'center',
                                    gap: 1,
                                    marginBlockStart: 2,
                                }}
                            >
                                <Information
                                    size="sm"
                                    aria-hidden
                                    variant="stroke"
                                    color="border.status.critical.strong"
                                />
                                <Text level={2} variant="alert">
                                    {gt.gettext('Please read the full terms to continue.')}
                                </Text>
                            </Flex>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
